import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { TextAreaField, TextField } from '../../components/Field';
import { SkeletonTable } from '../../components/Skeleton';
import type { CustomFieldDefinition, CustomFieldObjectType, CustomFieldType } from './api';
import {
  useCreateCustomField,
  useCustomFields,
  useDeleteCustomField,
  useUpdateCustomField,
} from './queries';
import { Select } from '../../components/Select';

const OBJECT_TYPES: CustomFieldObjectType[] = ['ticket', 'customer', 'contact'];

const FIELD_TYPES: CustomFieldType[] = [
  'text',
  'textarea',
  'integer',
  'decimal',
  'boolean',
  'date',
  'datetime',
  'select',
  'multiselect',
  'url',
  'email',
];

const TYPES_WITH_OPTIONS = new Set<CustomFieldType>(['select', 'multiselect']);

/**
 * Die Festlegung eigener Felder -- K-26.
 *
 * Schluessel und Feldtyp lassen sich nur beim Anlegen setzen. Das ist keine
 * Bequemlichkeit der Umsetzung: Ein geaenderter Schluessel braeche jeden
 * Bericht, der ihn benutzt, und ein geaenderter Typ machte jeden bereits
 * erfassten Wert ungueltig. Die Oberflaeche sagt das an den Feldern, statt
 * den Anwender in eine Fehlermeldung laufen zu lassen.
 */
export function CustomFieldsPage() {
  const { t } = useTranslation();
  const definitions = useCustomFields();
  const create = useCreateCustomField();
  const update = useUpdateCustomField();
  const remove = useDeleteCustomField();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CustomFieldDefinition | null>(null);

  return (
    <>
      <PageHeader
        title={t('customfields.title')}
        subtitle={t('customfields.subtitle')}
        action={
          <Button
            variant="primary"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            {t('customfields.add')}
          </Button>
        }
      />

      {definitions.isError ? <ErrorNotice error={definitions.error} /> : null}
      {remove.isError ? <ErrorNotice error={remove.error} /> : null}

      <div className="card card--flush">
        {definitions.isPending ? <SkeletonTable columns={7} /> : null}

        {definitions.data?.length === 0 ? (
          <EmptyState
            icon="customFields"
            title={t('customfields.empty')}
            hint={t('customfields.emptyHint')}
          />
        ) : null}

        {definitions.data && definitions.data.length > 0 ? (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('customfields.label')}</th>
                  <th scope="col">{t('customfields.key')}</th>
                  <th scope="col">{t('customfields.objectType')}</th>
                  <th scope="col">{t('customfields.fieldType')}</th>
                  <th scope="col">{t('customfields.isRequired')}</th>
                  <th scope="col">{t('customfields.isActive')}</th>
                  <th scope="col">
                    <span className="visually-hidden">{t('masterdata.edit')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {definitions.data.map((definition) => (
                  <tr key={definition.id}>
                    <td className="table__subject">{definition.label}</td>
                    <td className="table__number">{definition.key}</td>
                    <td>{t(`customfields.objectType${capitalise(definition.object_type)}`)}</td>
                    <td>{t(`customfields.type${capitalise(definition.field_type)}`)}</td>
                    <td>
                      {definition.is_required ? (
                        <Badge tone="info">{t('masterdata.yes')}</Badge>
                      ) : (
                        <span className="table__muted">{t('app.none')}</span>
                      )}
                    </td>
                    <td>
                      {definition.is_active ? (
                        <Badge tone="success">{t('masterdata.yes')}</Badge>
                      ) : (
                        <span className="table__muted">{t('app.none')}</span>
                      )}
                    </td>
                    <td>
                      <div className="row" style={{ justifyContent: 'flex-end' }}>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setEditing(definition);
                            setDialogOpen(true);
                          }}
                        >
                          {t('masterdata.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          busy={remove.isPending && remove.variables === definition.id}
                          onClick={() => {
                            if (window.confirm(t('masterdata.deleteConfirm'))) {
                              remove.mutate(definition.id);
                            }
                          }}
                        >
                          {t('masterdata.delete')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {dialogOpen ? (
        <CustomFieldDialog
          definition={editing}
          busy={create.isPending || update.isPending}
          error={create.isError ? create.error : update.isError ? update.error : null}
          onClose={() => {
            setDialogOpen(false);
            setEditing(null);
          }}
          onSubmit={(values) => {
            if (editing) {
              update.mutate(
                { id: editing.id, payload: values },
                {
                  onSuccess: () => {
                    setDialogOpen(false);
                    setEditing(null);
                  },
                },
              );
            } else {
              create.mutate(values, {
                onSuccess: () => {
                  setDialogOpen(false);
                },
              });
            }
          }}
        />
      ) : null}
    </>
  );
}

interface DialogProps {
  definition: CustomFieldDefinition | null;
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (values: Record<string, unknown>) => void;
}

function CustomFieldDialog({ definition, busy, error, onClose, onSubmit }: DialogProps) {
  const { t } = useTranslation();
  const isEdit = definition !== null;

  const [objectType, setObjectType] = useState<CustomFieldObjectType>(
    definition?.object_type ?? 'ticket',
  );
  const [fieldType, setFieldType] = useState<CustomFieldType>(definition?.field_type ?? 'text');
  const [key, setKey] = useState(definition?.key ?? '');
  const [label, setLabel] = useState(definition?.label ?? '');
  const [helpText, setHelpText] = useState(definition?.help_text ?? '');
  const [options, setOptions] = useState((definition?.options ?? []).join('\n'));
  const [isRequired, setIsRequired] = useState(definition?.is_required ?? false);
  const [isActive, setIsActive] = useState(definition?.is_active ?? true);

  const needsOptions = TYPES_WITH_OPTIONS.has(fieldType);

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="dialog__overlay" />
        <Dialog.Content className="card dialog__content">
          <Dialog.Title asChild>
            <h2 style={{ marginBottom: 'var(--space-4)' }}>
              {isEdit ? t('masterdata.edit') : t('customfields.add')}
            </h2>
          </Dialog.Title>
          <Dialog.Description className="visually-hidden">
            {t('customfields.subtitle')}
          </Dialog.Description>

          {error ? <ErrorNotice error={error} /> : null}

          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              const parsedOptions = options
                .split('\n')
                .map((line) => line.trim())
                .filter(Boolean);

              onSubmit(
                isEdit
                  ? {
                      label,
                      help_text: helpText,
                      is_required: isRequired,
                      is_active: isActive,
                      ...(needsOptions ? { options: parsedOptions } : {}),
                    }
                  : {
                      object_type: objectType,
                      key,
                      label,
                      field_type: fieldType,
                      help_text: helpText,
                      options: needsOptions ? parsedOptions : [],
                      is_required: isRequired,
                      is_active: isActive,
                    },
              );
            }}
          >
            {!isEdit ? (
              <>
                <div className="field">
                  <label className="field__label" htmlFor="cf-object-type">
                    {t('customfields.objectType')}
                  </label>
                  <Select
                    id="cf-object-type"
                    className="field__control"
                    value={objectType}
                    onChange={(event) => {
                      setObjectType(event.target.value as CustomFieldObjectType);
                    }}
                  >
                    {OBJECT_TYPES.map((value) => (
                      <option key={value} value={value}>
                        {t(`customfields.objectType${capitalise(value)}`)}
                      </option>
                    ))}
                  </Select>
                </div>

                <TextField
                  label={t('customfields.key')}
                  hint={t('customfields.keyHint')}
                  value={key}
                  onChange={(event) => {
                    setKey(event.target.value);
                  }}
                />

                <div className="field">
                  <label className="field__label" htmlFor="cf-field-type">
                    {t('customfields.fieldType')}
                  </label>
                  <Select
                    id="cf-field-type"
                    className="field__control"
                    value={fieldType}
                    onChange={(event) => {
                      setFieldType(event.target.value as CustomFieldType);
                    }}
                  >
                    {FIELD_TYPES.map((value) => (
                      <option key={value} value={value}>
                        {t(`customfields.type${capitalise(value)}`)}
                      </option>
                    ))}
                  </Select>
                  <span className="field__hint">{t('customfields.fieldTypeHint')}</span>
                </div>
              </>
            ) : null}

            <TextField
              label={t('customfields.label')}
              value={label}
              onChange={(event) => {
                setLabel(event.target.value);
              }}
            />

            <TextAreaField
              label={t('customfields.helpText')}
              rows={2}
              value={helpText}
              onChange={(event) => {
                setHelpText(event.target.value);
              }}
            />

            {needsOptions ? (
              <TextAreaField
                label={t('customfields.options')}
                hint={t('customfields.optionsHint')}
                rows={4}
                value={options}
                onChange={(event) => {
                  setOptions(event.target.value);
                }}
              />
            ) : null}

            <div className="field">
              <label className="row" style={{ gap: 'var(--space-2)' }}>
                <input
                  type="checkbox"
                  checked={isRequired}
                  onChange={(event) => {
                    setIsRequired(event.target.checked);
                  }}
                />
                <span className="field__label" style={{ margin: 0 }}>
                  {t('customfields.isRequired')}
                </span>
              </label>
            </div>

            <div className="field">
              <label className="row" style={{ gap: 'var(--space-2)' }}>
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(event) => {
                    setIsActive(event.target.checked);
                  }}
                />
                <span className="field__label" style={{ margin: 0 }}>
                  {t('customfields.isActive')}
                </span>
              </label>
            </div>

            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={onClose}>
                {t('app.cancel')}
              </Button>
              <Button type="submit" variant="primary" busy={busy}>
                {t('app.save')}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** ``service_request`` wird zu ``ServiceRequest`` -- passend zu den i18n-Schluesseln. */
function capitalise(value: string): string {
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}
